import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import axios from 'axios'
import {
  Box, Stack, Typography, Paper, Button, TextField, Select, MenuItem,
  FormControl, InputLabel, Chip, Switch, FormControlLabel, Alert,
  CircularProgress, Divider, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, useMediaQuery, useTheme,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import InboxIcon from '@mui/icons-material/Inbox'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import PublicIcon from '@mui/icons-material/Public'
import ToneReviewPanel from './components/ToneReviewPanel'
import { FS, SP, V2, inkFor, hoverOnly, focusRing } from '../theme/tokens'
// R16-W1 Runde 3: das Datum der Pending-Zeile steht im A11y-Namen (die
// Nachfahren von `role="button"` sind praesentational). Dieselbe Berliner
// Tagesform, die `<CaseTime precision="date">` sichtbar rendert.
import { formatDate } from '../utils/format'
import { v2Tone } from '../theme/theme'
import CaseTime from '../components/common/CaseTime'
import RefreshButton from '../components/common/RefreshButton'
import PageContainer from '../components/common/PageContainer'
import ViewHeader from '../components/ViewHeader'
import CardListSkeleton from '../Skeletons/CardListSkeleton'
import UnderlineTabs from '../components/common/UnderlineTabs'
import ListToolbar from '../components/common/ListToolbar'
import BottomSheetSelect from '../components/common/BottomSheetSelect'
import EmptyState from '../components/common/EmptyState'
import TableFooterSimple from '../components/common/TableFooterSimple'
import RowActionsMenu from '../components/common/RowActionsMenu'
import { ConfirmDialog } from '../components/common/confirm'
import useListQuery from '../components/common/listQuery/useListQuery'
import DisabledReason from '../components/common/DisabledReason';

const API = process.env.REACT_APP_API_BASE_URL

const CATEGORIES = [
  { value: 'shipping',  label: 'Shipping' },
  { value: 'returns',   label: 'Returns' },
  { value: 'brand',     label: 'Brand Voice' },
  { value: 'products',  label: 'Products' },
  { value: 'faq',       label: 'FAQ' },
  { value: 'website',   label: 'Website Sync' },
]

const CATEGORY_COLORS = {
  shipping: 'info',
  returns: 'warning',
  brand: 'secondary',
  products: 'primary',
  faq: 'success',
  website: 'default',
}

// Audience (migration 195) — how far a document may travel. The list is the
// same set the backend validates against (`_AUDIENCE_VALUES`,
// backend/inquiry_routes.py); adding a value here without adding it there
// produces a 400 on save.
//
// `tone` resolves through `theme.palette.v2` (the measured canon), NOT through
// MUI stock colours: `color="info"` / `color="warning"` filled rendered white
// on rgb(2,136,209) / rgb(237,108,2) — 3.86:1 and 3.11:1 at FS.xs (11.5 px),
// both under the 4.5:1 AA floor for normal text.
//
// `Icon` exists so the level is legible without colour (WCAG 1.4.1): the
// category chip in the same row carries the same MUI colour families, so
// colour alone said nothing about WHICH axis a chip belonged to.
const AUDIENCES = [
  {
    value: 'internal', label: 'Internal', tone: 'plain', Icon: LockOutlinedIcon,
    meaning: 'staff only',
  },
  {
    value: 'customer', label: 'Customer', tone: 'info', Icon: PersonOutlineIcon,
    meaning: 'may be quoted in a reply to a customer',
  },
  {
    value: 'public', label: 'Public', tone: 'warn', Icon: PublicIcon,
    meaning: 'may be shown publicly',
  },
]

// Strictest level first — a document whose audience was never set stays here.
const AUDIENCE_DEFAULT = 'internal'

/**
 * Die EINE Stelle, die entscheidet, welche Stufe eine Zeile hat.
 *
 * Fehlend, `null` und ein unbekannter Wert landen alle auf der STRENGSTEN Stufe —
 * `audience` entscheidet, ob ein Dokument einem Kunden gezeigt werden darf, und
 * eine unklare Angabe darf dabei nie zugunsten der breiteren Stufe ausgehen.
 *
 * Chip und Filter riefen das frueher getrennt auf: der Chip zeigte einen
 * unbekannten Wert in der strengsten Optik, der Filter verglich ihn roh — die
 * Zeile fiel damit aus JEDER Ansicht heraus, und ausgerechnet die unklaren
 * Zeilen wurden unsichtbar (Regel-16-Warnung W3).
 */
function audienceLevel(value) {
  return AUDIENCES.some(a => a.value === value) ? value : AUDIENCE_DEFAULT
}

// The full legend — for the EDITOR, where all three levels are the choice being
// made. It is deliberately NOT what the list chips carry: a chip that repeats
// all three definitions never answers "what does THIS one mean".
const AUDIENCE_HELP = `${AUDIENCES.map(a => `${a.label} = ${a.meaning}`).join(' · ')}.`

// One chip's own line — name of the axis first, so the word carries even when
// the colour does not (and when the chip is read out rather than seen).
const audienceLine = (label, meaning) => `Audience: ${label} — ${meaning}`

/**
 * The audience chip's look. Soft fill + full tone border + tone text, i.e. the
 * v2 canon's chip treatment — which is measured (`tokens.js`: main-on-soft is
 * 4.75:1 light / 4.59:1 dark at the worst tone) and, just as importantly, is a
 * DIFFERENT treatment from the saturated filled category chip beside it.
 */
const audienceChipSx = (tone) => (t) => {
  const plain = tone === 'plain'
  const dark = t.palette.mode === 'dark'
  const v2 = plain ? null : v2Tone(t, tone)
  return {
    fontSize: FS.xs,
    height: 20,
    fontWeight: 600,
    bgcolor: plain ? (dark ? V2.washDark : V2.wash) : v2.soft,
    color: plain ? inkFor(t.palette.mode).ink70 : v2.main,
    border: '1px solid',
    borderColor: plain ? (dark ? V2.washSelectedDark : V2.washSelected) : v2.c100,
    '& .MuiChip-icon': { color: 'inherit', fontSize: 12, ml: 0.5, mr: -0.25 },
    // The chip is focusable so the Tooltip is reachable without a mouse
    // (WCAG 2.1.1) — which means it needs a visible ring of its own.
    '&:focus-visible': focusRing(),
  }
}

const INQUIRY_TYPES = [
  { value: 'product',  label: 'Product Advisory' },
  { value: 'delivery', label: 'Delivery Status' },
  { value: 'policy',   label: 'Return Policy' },
  { value: 'general',  label: 'General' },
]

// Die zwei Reiter — die REIHENFOLGE ist die Indexabbildung (`UnderlineTabs`
// arbeitet mit dem Index, die Adresse mit dem Schluessel). Der erste Schluessel
// ist die Vorgabe und steht ohne Parameter in der Adresse (§3.1).
const TAB_KEYS = ['knowledge', 'tone']
const TABS = [{ label: 'Knowledge Docs' }, { label: 'Tone Examples' }]

// ── Knowledge Doc Editor Dialog ───────────────────────────────────────────────
function DocEditDialog({ open, doc, onClose, onSaved }) {
  const [form, setForm] = useState({
    category: 'faq', title: '', body: '', source_url: '', is_active: true,
    audience: AUDIENCE_DEFAULT,
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (doc) {
      setForm({
        category: doc.category || 'faq',
        title: doc.title || '',
        body: doc.body || '',
        source_url: doc.source_url || '',
        is_active: doc.is_active !== false,
        // A doc from before migration 195 (or one the API did not serialize)
        // falls back to the STRICTEST level, never to the widest one.
        audience: doc.audience || AUDIENCE_DEFAULT,
      })
    } else {
      setForm({
        category: 'faq', title: '', body: '', source_url: '', is_active: true,
        audience: AUDIENCE_DEFAULT,
      })
    }
    setError(null)
  }, [doc, open])

  const handleSave = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      setError('Title and body are required')
      return
    }
    setSaving(true)
    setError(null)
    try {
      if (doc?.id) {
        // The endpoint is key-exact partial (`if field in data`,
        // backend/inquiry_routes.py) and has no optimistic-concurrency check.
        // Sending `audience` on every save is therefore a path on which a level
        // silently gets WIDER again: a dialog opened while the doc was `public`
        // would write `public` back over an `internal` set elsewhere in the
        // meantime. Only send the field when this dialog actually changed it.
        const payload = { ...form }
        if (payload.audience === (doc.audience || AUDIENCE_DEFAULT)) {
          delete payload.audience
        }
        await axios.put(`${API}/api/inquiry/knowledge/${doc.id}`, payload)
      } else {
        // A new document always declares its level — the row does not exist yet,
        // so there is nothing to widen.
        await axios.post(`${API}/api/inquiry/knowledge`, form)
      }
      onSaved()
      onClose()
    } catch (e) {
      setError(e.response?.data?.error || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const tokenEst = Math.max(1, Math.round((form.body || '').length / 4))

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: FS.base, fontWeight: 600 }}>
        {doc?.id ? 'Edit Knowledge Document' : 'New Knowledge Document'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={SP.md} pt={1}>
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
          <Stack direction="row" spacing={SP.sm}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Category</InputLabel>
              <Select
                label="Category"
                value={form.category}
                onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
              >
                {CATEGORIES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              label="Title"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              size="small"
              fullWidth
            />
          </Stack>
          <Stack direction="row" spacing={SP.sm} alignItems="center" flexWrap="wrap" useFlexGap>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="doc-audience-label">Audience</InputLabel>
              <Select
                labelId="doc-audience-label"
                label="Audience"
                value={form.audience}
                onChange={e => setForm(f => ({ ...f, audience: e.target.value }))}
                inputProps={{ 'aria-describedby': 'doc-audience-help' }}
              >
                {AUDIENCES.map(a => <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>)}
              </Select>
            </FormControl>
            <Typography id="doc-audience-help" sx={{ fontSize: FS.xs, color: 'text.secondary', flex: 1, minWidth: 220 }}>
              {AUDIENCE_HELP}
            </Typography>
          </Stack>
          <TextField
            label="Body (Markdown)"
            value={form.body}
            onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
            multiline
            minRows={10}
            maxRows={30}
            fullWidth
            inputProps={{ style: { fontSize: FS.sm, fontFamily: 'monospace' } }}
            helperText={`~${tokenEst} tokens`}
          />
          <TextField
            label="Source URL (optional — if synced from webshop page)"
            value={form.source_url}
            onChange={e => setForm(f => ({ ...f, source_url: e.target.value }))}
            size="small"
            fullWidth
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.is_active}
                onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
                size="small"
              />
            }
            label={<Typography sx={{ fontSize: FS.sm }}>Active (included in prompts)</Typography>}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} size="small">Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          size="small"
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
        >
          {saving ? 'Saving…' : 'Save document'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ── Tone Example Card ─────────────────────────────────────────────────────────
function ToneExampleCard({ example, onToggleFeatured, onDeactivate }) {
  const [expanded, setExpanded] = useState(false)
  const theme = useTheme()
  const typ = INQUIRY_TYPES.find(t => t.value === example.inquiry_type)?.label || example.inquiry_type
  const sprache = example.language?.toUpperCase()
  const zustand = example.is_featured ? 'Featured' : (example.is_active ? 'Active' : 'Inactive')
  return (
    <Paper variant="outlined" sx={{ p: SP.card }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Stack direction="row" spacing={SP.chip} alignItems="center" flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
          <Chip
            label={typ}
            size="small"
            color="primary"
            sx={{ fontSize: FS.xs, height: 20 }}
          />
          <Chip
            label={sprache}
            size="small"
            variant="outlined"
            sx={{ fontSize: FS.xs, height: 20 }}
          />
          {/* 🔴 P22: der Zustand traegt ein WORT — „Featured" war bis G14b-2 ein
              farbiger Chip, „Active"/„Inactive" standen gar nicht da.
              🔴 R16-B1 (2026-10-31): „Featured" stand in `warning.main` — das
              sind gemessen 3,113:1 auf `paper` (hell) und damit unter 4,5:1,
              die WCAG 1.4.3 fuer kleinen Text verlangt; dieselbe Zahl steht im
              Docstring von `v2ToneHover` (`theme.js`) und wurde von einem
              frueheren Regel-16-Gate geruegt (`PODetail.jsx`). Jetzt der
              v2-Warnton: 5,022:1 hell, 8,763:1 dunkel. */}
          <Box component="span" data-testid={`karte-status-${example.id}`}>
            <Typography
              component="span"
              sx={{ fontSize: FS.xs, color: example.is_featured ? v2Tone(theme, 'warn').main : 'text.secondary' }}
            >
              {zustand}
            </Typography>
          </Box>
          {/* Track A5-4b: knowledge-base rows are an ARCHIVE — when an example
              was captured is a record fact, so absolute at day granularity.
              A "vor 3 Tagen" primary reading would say something about now
              rather than about the row. */}
          <Typography sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
            <Box component="span" data-testid={`karte-kennung-${example.id}`} sx={{ fontFamily: 'monospace' }}>
              {example.id}
            </Box>
            {' · '}
            <CaseTime value={example.created_at} variant="absolute" precision="date" />
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0} sx={{ flexShrink: 0 }}>
          {/* P19: EINE Aktionsflaeche je Karte, mit Zeilenbezug im Namen.
              🔴 E2 (2026-10-06): „Deactivate" ist RUECKNEHMBAR — die Route setzt
              `is_active = False` (soft delete, `inquiry_routes.py:3106`),
              die Zeile bleibt als „Inactive" stehen. Deshalb neutral (kein
              `danger`) und ohne Papierkorb. */}
          <RowActionsMenu
            label={`Actions for ${typ} ${sprache || ''}`.trim()}
            actions={[
              {
                key: 'featured',
                label: example.is_featured ? 'Remove from featured' : 'Mark as featured',
                onClick: () => onToggleFeatured(example),
              },
              {
                key: 'deactivate',
                label: 'Deactivate',
                onClick: () => onDeactivate(example.id),
              },
            ]}
          />
        </Stack>
      </Stack>
      {example.notes && (
        <Typography data-testid={`karte-titel-${example.id}`} sx={{ fontSize: FS.xs, color: 'text.secondary', mt: 0.5 }}>
          {example.notes}
        </Typography>
      )}
      <Button
        size="small"
        variant="text"
        sx={{ mt: 0.5, fontSize: FS.xs, p: 0 }}
        onClick={() => setExpanded(v => !v)}
      >
        {expanded ? 'Hide preview' : 'Preview'}
      </Button>
      {expanded && (
        <Box mt={SP.sm}>
          <Typography sx={{ fontSize: FS.xs, fontWeight: 600, mb: 0.25 }}>Customer:</Typography>
          <Typography sx={{ fontSize: FS.xs, whiteSpace: 'pre-wrap', color: 'text.secondary', maxHeight: 120, overflow: 'auto' }}>
            {example.customer_email?.slice(0, 400)}
          </Typography>
          <Typography sx={{ fontSize: FS.xs, fontWeight: 600, mt: 1, mb: 0.25 }}>Samuel's reply:</Typography>
          <Typography sx={{ fontSize: FS.xs, whiteSpace: 'pre-wrap', color: 'text.secondary', maxHeight: 200, overflow: 'auto' }}>
            {example.samuel_reply?.slice(0, 600)}
          </Typography>
        </Box>
      )}
    </Paper>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function InquiryKnowledgeAdmin() {
  const navigate = useNavigate()
  const theme = useTheme()
  // 🔴 Die Facetten stehen am Desktop IN Z1 (`ListToolbar.facets`), auf dem
  // Telefon oeffnen sie das Blatt (`moreFilters`) — `ListToolbar` rendert
  // Facetten unter `sm` nicht (`facettenKnoten = !z1Kompakt && …`). Dieselbe
  // Weiche wie in `RefundsReport`/`VendorsOverview`.
  const schmal = useMediaQuery(theme.breakpoints.down('sm'))
  // Der Rueckfrageweg der Ruecknahme (Soft-Delete). `InlineConfirmButton` kann
  // nicht in einem Menue stehen — der Schritt bleibt, die Form ist ein Dialog.
  const [bestaetigen, setBestaetigen] = useState(null)

  // Knowledge docs
  // 🔴 R16 N1: `null` heisst „noch nie geladen", `[]` hiesse „geladen und
  // leer". Eine frische Wissensdatenbank ist berechtigt leer — mit `[]` laese
  // der erste Neulauf als Erstaufbau, und der Knopf sperrte, statt zu drehen.
  const [docs, setDocs] = useState(null)
  const [docsLoading, setDocsLoading] = useState(true)
  // 🔴 Der Ausgang des Abrufs wird SICHTBAR (Plan §2.3 Nr. 8). Bis G14b-2 stand
  // hier `catch { /* non-critical */ }` — die Seite zeigte dann still eine leere
  // Liste, und „nichts da" war von „konnte nicht laden" nicht zu unterscheiden.
  const [docsError, setDocsError] = useState(null)
  const [editDoc, setEditDoc] = useState(null)     // null = new, object = edit
  const [docDialogOpen, setDocDialogOpen] = useState(false)

  // Tone examples
  // 🔴 R16 N1: `null` = „noch nie geladen" (siehe `docs` oben).
  const [examples, setExamples] = useState(null)
  const [examplesLoading, setExamplesLoading] = useState(true)
  const [examplesError, setExamplesError] = useState(null)

  // Thread import
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [importForm, setImportForm] = useState({ inquiry_type: 'general', language: 'de', notes: '', thread_json: '' })
  const [importing, setImporting] = useState(false)
  const [importError, setImportError] = useState(null)

  const [reviewExample, setReviewExample] = useState(null)
  const [reviewOpen, setReviewOpen] = useState(false)

  // ── Der Reiter lebt in der ADRESSE (Plan §2.3 Nr. 3, §3.1) ────────────────
  // Bis G14b-2 stand er in `useState`: ein geteilter Link zeigte immer den
  // Dokumente-Reiter, und ein Neuladen warf die Auswahl weg. Wechsel = `push`
  // (N10.2) — Zurueck geht damit auf den vorigen Reiter.
  // 🔴 `setSearchParams({tab})` bzw. `{}` schreibt ALLE Listenparameter weg: die
  // beiden Reiter haben VERSCHIEDENE Dimensionen, ein `category` im Ton-Reiter
  // waere eine Filterangabe ohne Wirkung und ohne Chip (§2.3 Nr. 3).
  const [searchParams, setSearchParams] = useSearchParams()
  const tabRoh = searchParams.get('tab')
  const tab = TAB_KEYS.includes(tabRoh) ? tabRoh : TAB_KEYS[0]
  const tabIndex = TAB_KEYS.indexOf(tab)
  // 🔴 Ein unbekannter Reiter wird VERWORFEN und EINMAL gemeldet (N10.4/P13):
  // die Adresse faellt auf die Vorgabe zurueck (`replace`), die Meldung bleibt
  // stehen, bis der Nutzer sie schliesst.
  const [tabHinweis, setTabHinweis] = useState(null)
  useEffect(() => {
    if (tabRoh && !TAB_KEYS.includes(tabRoh)) {
      setSearchParams({}, { replace: true })
      setTabHinweis(tabRoh)
    }
  }, [tabRoh, setSearchParams])
  const wechsleReiter = useCallback((i) => {
    const key = TAB_KEYS[i] ?? TAB_KEYS[0]
    setSearchParams(key === TAB_KEYS[0] ? {} : { tab: key })
  }, [setSearchParams])

  // 🔴 Beide Listen werden UNGEFILTERT geholt und im Browser gefiltert (§2.3
  // Nr. 6, E4). Nur so ist `M` — der Bestand ohne Filter — bekannt, und der
  // Trefferzaehler sagt die Wahrheit (§4.2). Die Filterung ist seitenweise
  // unten; der Server sieht KEINEN Filterparameter.
  const fetchDocs = useCallback(async () => {
    setDocsLoading(true)
    try {
      const res = await axios.get(`${API}/api/inquiry/knowledge`)
      setDocs(res.data.docs || [])
      setDocsError(null)
    } catch (e) {
      setDocsError(e.response?.data?.error || e.message || 'Could not load knowledge documents.')
    } finally { setDocsLoading(false) }
  }, [])

  const fetchExamples = useCallback(async () => {
    setExamplesLoading(true)
    try {
      const res = await axios.get(`${API}/api/inquiry/tone-examples?active_only=false`)
      setExamples(res.data.examples || [])
      setExamplesError(null)
    } catch (e) {
      setExamplesError(e.response?.data?.error || e.message || 'Could not load tone examples.')
    } finally { setExamplesLoading(false) }
  }, [])

  useEffect(() => { fetchDocs() }, [fetchDocs])
  useEffect(() => { fetchExamples() }, [fetchExamples])

  const handleDeleteDoc = async (id) => {
    await axios.delete(`${API}/api/inquiry/knowledge/${id}`)
    fetchDocs()
  }

  const handleToggleFeatured = async (ex) => {
    await axios.put(`${API}/api/inquiry/tone-examples/${ex.id}`, { is_featured: !ex.is_featured })
    fetchExamples()
  }

  const handleDeactivateExample = async (id) => {
    await axios.delete(`${API}/api/inquiry/tone-examples/${id}`)
    fetchExamples()
  }

  // Der Rueckfrageweg der Ruecknahme: erst der Dialog, dann die Aktion.
  const bestaetigungAusfuehren = async () => {
    if (!bestaetigen) return
    const { art, id } = bestaetigen
    setBestaetigen(null)
    if (art === 'doc') await handleDeleteDoc(id)
    else await handleDeactivateExample(id)
  }

  const [pulling, setPulling] = useState(false)
  const [pullStatus, setPullStatus] = useState(null)

  const handlePullToneInbox = async () => {
    setPulling(true)
    setPullStatus(null)
    try {
      const res = await axios.post(`${API}/api/inquiry/process-tone-inbox`)
      setPullStatus(res.data)
      fetchExamples()
    } catch (e) {
      setPullStatus({ error: e.response?.data?.error || 'Pull failed' })
    } finally {
      setPulling(false)
    }
  }

  const handleImportThread = async () => {
    setImporting(true)
    setImportError(null)
    try {
      const thread = JSON.parse(importForm.thread_json)
      await axios.post(`${API}/api/inquiry/tone-examples/import-thread`, {
        thread,
        inquiry_type: importForm.inquiry_type,
        language: importForm.language,
        notes: importForm.notes,
      })
      setImportDialogOpen(false)
      fetchExamples()
    } catch (e) {
      setImportError(e.response?.data?.error || 'Import failed — check JSON format')
    } finally {
      setImporting(false)
    }
  }

  // ── Der Abfragezustand: JE REITER eine Instanz (§3.1) ─────────────────────
  // Die beiden Reiter haben VERSCHIEDENE Dimensionen; zwei Instanzen halten sie
  // getrennt. Ein gemeinsamer Hook mit sechs Dimensionen zeigte im
  // Dokumente-Reiter einen `inquiry_type`-Chip ohne Wirkung (und umgekehrt).
  // 🔴 Kein `sort` (§3.3): D6 sortiert nicht, die Reihenfolge ist die des
  // Servers. `page` ist 1-basiert in der Adresse, 0-basiert am Baustein (§3.2).
  const knowledgeDims = useMemo(() => [
    { key: 'q', kind: 'single', label: 'Search' },
    { key: 'category', kind: 'single', label: 'Category', values: CATEGORIES },
    { key: 'audience', kind: 'single', label: 'Audience', values: AUDIENCES },
  ], [])
  const toneDims = useMemo(() => [
    { key: 'q', kind: 'single', label: 'Search' },
    { key: 'inquiry_type', kind: 'single', label: 'Inquiry type', values: INQUIRY_TYPES },
    { key: 'language', kind: 'single', label: 'Language', values: [{ value: 'de', label: 'DE' }, { value: 'en', label: 'EN' }] },
  ], [])
  const knowledgeQuery = useListQuery({ dimensions: knowledgeDims })
  const toneQuery = useListQuery({ dimensions: toneDims })

  // 🔴 „Clear all" kapselt den REITER (§2.3 Nr. 3, Grill B1): `clearAll` loescht
  // `tab` von Bauart wegen (`useListQuery.js:291`). Der Hook kennt dafuer
  // `keepTab` — EIN Schreibvorgang mit `replace`, kein zweiter Push (§3.2).
  // Der Fokus geht danach ins Suchfeld (erster Z1-Bedienteil, §5.3b Nr. 4).
  const clearKnowledge = useCallback(() => knowledgeQuery.clearAll({ keepTab: true }), [knowledgeQuery])
  const clearTone = useCallback(() => toneQuery.clearAll({ keepTab: true }), [toneQuery])

  const docsBasis = docs ?? []
  const examplesBasis = examples ?? []
  const gesuchterBegriff = (knowledgeQuery.state.q || '').trim().toLowerCase()
  const gesuchterTon = (toneQuery.state.q || '').trim().toLowerCase()

  const filteredDocs = docsBasis.filter((d) => {
    if (gesuchterBegriff) {
      const heu = `${d.title || ''} ${d.body || ''}`.toLowerCase()
      if (!heu.includes(gesuchterBegriff)) return false
    }
    if (knowledgeQuery.state.category && d.category !== knowledgeQuery.state.category) return false
    // A row without an audience counts as `internal` — same fallback the
    // editor applies, so the filter and the form never disagree.
    if (knowledgeQuery.state.audience && audienceLevel(d.audience) !== knowledgeQuery.state.audience) return false
    return true
  })

  // Der Ton-Reiter sucht in Notiz, Thema der Zusammenfassung und Anfragetyp
  // (§2.3 Nr. 4) — alles drei ist auf der Karte sichtbar.
  const themaVon = (ex) => {
    if (!ex.llm_thread_summary) return ''
    try {
      const s = typeof ex.llm_thread_summary === 'string' ? JSON.parse(ex.llm_thread_summary) : ex.llm_thread_summary
      return s?.topic || ''
    } catch { return '' }
  }
  const typLabel = (wert) => INQUIRY_TYPES.find(t => t.value === wert)?.label || wert || ''

  const filteredExamples = examplesBasis.filter((ex) => {
    if (gesuchterTon) {
      const heu = `${ex.notes || ''} ${themaVon(ex)} ${typLabel(ex.inquiry_type)} ${ex.inquiry_type || ''}`.toLowerCase()
      if (!heu.includes(gesuchterTon)) return false
    }
    if (toneQuery.state.inquiry_type && ex.inquiry_type !== toneQuery.state.inquiry_type) return false
    if (toneQuery.state.language && ex.language !== toneQuery.state.language) return false
    return true
  })

  // 🔴 Die Token-Summe bleibt die heutige Bezugsmenge: ALLE aktiven Dokumente,
  // UNGEFILTERT (§2.3 Nr. 11, Grill W3). Das Etikett nennt sie, damit der
  // Ortswechsel (Filterzeile → Fusszeile) die Zahl nicht umdeutet.
  const totalTokens = docsBasis.filter(d => d.is_active).reduce((s, d) => s + (d.token_estimate || 0), 0)

  const SEITENGROESSE = 50
  const seiteFuer = (treffer, query) => {
    const seiten = Math.max(1, Math.ceil(treffer / SEITENGROESSE))
    return Math.min(query.state.page || 1, seiten)
  }
  const docsSeite = seiteFuer(filteredDocs.length, knowledgeQuery)
  const docsAufSeite = filteredDocs.slice((docsSeite - 1) * SEITENGROESSE, docsSeite * SEITENGROESSE)

  // Der Ton-Reiter zeigt die AKTIVEN Beispiele als Kartenliste; die
  // unbestaetigten stehen als eigener Abschnitt darueber (§1.5, Nr. 13).
  // Seitenwechsel und Zaehler beschreiben die Kartenliste.
  const toneAktivBasis = examplesBasis.filter(e => e.is_active)
  const toneAktivGefiltert = filteredExamples.filter(e => e.is_active)
  const tonePendingGefiltert = filteredExamples.filter(e => !e.is_active)
  const toneSeite = seiteFuer(toneAktivGefiltert.length, toneQuery)
  const toneAufSeite = toneAktivGefiltert.slice((toneSeite - 1) * SEITENGROESSE, toneSeite * SEITENGROESSE)

  return (
    <PageContainer width="full" boxSx={{ display: 'flex', flexDirection: 'column', gap: SP.section }}>
      <Box data-testid="liste-kopf">
        {/* 🔴 Seitenaktionen, KEINE Filter (Befund L7): sie stehen im Kopf, nicht
            zwischen den Kriterien. `ListToolbar` fuehrt keinen Platz fuer eine
            sichtbare Primaeraktion (nur `view` fuer Ansicht/Sortierung und
            `actions` als ⋮) — und auf dem Telefon wuerde ein `contained`-Knopf
            in Z1 die Suche unter ihren Boden druecken. Der Kopf ist die
            Hausform fuer Seitenaktionen und zeigt mobil eine eigene Zeile. */}
        <ViewHeader
          title="Inquiry Knowledge Base"
          breadcrumbs={[{ label: 'After-Sales', onClick: () => navigate('/rma') }, { label: 'Knowledge Base' }]}
          actions={tab === 'knowledge' ? [
            {
              key: 'neu', label: 'New document', emphasis: 'primary', startIcon: <AddIcon />,
              onClick: () => { setEditDoc(null); setDocDialogOpen(true) },
            },
          ] : [
            {
              key: 'pull', label: pulling ? 'Pulling…' : 'Pull tone inbox now',
              startIcon: <InboxIcon />, onClick: handlePullToneInbox, disabled: pulling,
            },
            {
              key: 'paste', label: 'Paste JSON', startIcon: <AddIcon />,
              onClick: () => setImportDialogOpen(true),
            },
          ]}
        />
      </Box>

      {/* ── Die Reiterleiste (BS11 `UnderlineTabs`, `withPanel`) ─────────────── */}
      {/* 🔴 `ariaLabel` ist PFLICHT: `withPanel` WIRFT ohne ihn
          (`UnderlineTabs.jsx:355-359`), und der Name ist zugleich der benannte
          `tablist`, den P30 verlangt. */}
      {/* 🔴 Keine Zaehler an den Reitern (§5.2: Werkzeug-Reiter tragen keinen).
          `counts={[null, null]}` ist KEIN Zaehler: es schaltet die BREITEN-
          RESERVE des Bausteins ein (der unsichtbare fette Zwilling,
          `UnderlineTabs.jsx` `reserviert`, `:231` und `:549-558`), ohne eine
          Zahl zu rendern.
          🔴 NACHBESSERUNG 1 (2026-10-30): hier stand „ohne sie wandert der
          Nachbarreiter beim Wechsel um die Fettbreite — genau die Zusicherung
          P11-Ersatz". Die FOLGE war zu stark: die Wanderung betraegt bei
          diesen zwei Beschriftungen („Knowledge Docs" / „Tone Examples") ≤ 1 px
          und liegt damit UNTER der Messschwelle der Probe — der Rotweg
          „`counts` entfernen" machte die x-Messung NICHT rot (gemessen vom
          Orchestrator am 2026-10-30). Die Reserve bleibt richtig (sie macht die
          Breite von der Schriftmetrik unabhaengig, statt auf eine Differenz zu
          hoffen, die beim naechsten Label kippt); gemessen wird sie jetzt
          STRUKTURELL im P11-Ersatz (`tests/listen/inquiry-knowledge.spec.ts`:
          der `aria-hidden`-Zwilling in `visibility: hidden` und Schnitt 600). */}
      <Box data-testid="liste-reiter">
        <UnderlineTabs
          withPanel
          ariaLabel="Knowledge sections"
          tabs={TABS}
          counts={[null, null]}
          value={tabIndex}
          onChange={wechsleReiter}
        >
          <Box data-testid="liste-panel" aria-busy={tab === 'knowledge' ? (docsLoading || undefined) : (examplesLoading || undefined)}>
            {tabHinweis && (
              <Alert severity="info" onClose={() => setTabHinweis(null)} sx={{ mb: SP.card, fontSize: FS.xs }}>
                {`Ignored unknown tab “${tabHinweis}”.`}
              </Alert>
            )}

            {/* ── Knowledge Docs tab ──────────────────────────────────────────── */}
            {tab === 'knowledge' && (
              <Stack spacing={SP.card}>
                <ListToolbar
                  shortcut="/"
                  search={{
                    value: knowledgeQuery.state.q ?? '',
                    onChange: (v) => knowledgeQuery.set('q', v),
                    onCommit: (v) => knowledgeQuery.set('q', v),
                    placeholder: 'Search documents…',
                    label: 'Search documents',
                    inputRole: 'searchbox',
                    // N8.5: der Fehler des ERSTEN Aufbaus sperrt die Suche.
                    disabled: Boolean(docsError) && docs === null,
                    // N2.9: ohne `flex` steht das Feld auf seiner Eigenbreite,
                    // und die erste Facette rueckt um den Rest nach rechts.
                    sx: { flex: 1, minWidth: 0 },
                  }}
                  facets={schmal ? undefined : [
                    <BottomSheetSelect
                      key="category"
                      label="Category"
                      value={knowledgeQuery.state.category ?? ''}
                      onChange={(v) => knowledgeQuery.set('category', v || null)}
                      options={CATEGORIES}
                      disabled={Boolean(docsError) && docs === null}
                    />,
                    <BottomSheetSelect
                      key="audience"
                      label="Audience"
                      value={knowledgeQuery.state.audience ?? ''}
                      onChange={(v) => knowledgeQuery.set('audience', v || null)}
                      options={AUDIENCES.map(a => ({ value: a.value, label: a.label }))}
                      disabled={Boolean(docsError) && docs === null}
                    />,
                  ]}
                  // Auf dem Telefon oeffnen die Facetten das Blatt (§2.3 Nr. 5);
                  // dieselben zwei Kriterien, deklarativ.
                  moreFilters={schmal ? {
                    // 🔴 R16-W1 (2026-10-31): der OEFFNER selbst wird gesperrt,
                    // nicht nur seine Kriterien — sonst oeffnet ein Tippen im
                    // Erstaufbau-Fehler ein Blatt, in dem beide Felder grau
                    // sind. Hausform: `MediaLibraryPage.jsx` (`disabled: sperre`).
                    disabled: Boolean(docsError) && docs === null,
                    // R16-W5: das Blatt ist am Telefon der EINZIGE Filterweg —
                    // ohne Zahl sieht man die Wirkung erst nach dem Schliessen.
                    resultCount: docs === null ? null : filteredDocs.length,
                    criteria: [
                      {
                        key: 'category', label: 'Category',
                        value: knowledgeQuery.state.category ?? '',
                        onChange: (v) => knowledgeQuery.set('category', v || null),
                        options: CATEGORIES,
                        disabled: Boolean(docsError) && docs === null,
                      },
                      {
                        key: 'audience', label: 'Audience',
                        value: knowledgeQuery.state.audience ?? '',
                        onChange: (v) => knowledgeQuery.set('audience', v || null),
                        options: AUDIENCES.map(a => ({ value: a.value, label: a.label })),
                        disabled: Boolean(docsError) && docs === null,
                      },
                    ],
                    // 🔴 R15-W3 Runde 2 (2026-10-31): das Blatt gatet am
                    // FILTERZUSTAND, nicht am Bestand. Vorher stand „Clear all"
                    // im Grundzustand mit vollem Bestand da und tat nichts —
                    // derselbe tote Ausweg, den B2 nebenan schliesst, eine
                    // Ebene tiefer. Hausform: `RMACasesDashboardBody.jsx:1110`
                    // (`isFiltered ? clearAllFilters : undefined`); der
                    // Filterzustand ist `chips.length > 0` (Hausregel des
                    // Hooks, `useListQuery.js:145-147`).
                    onClearAll: knowledgeQuery.chips.length > 0 ? clearKnowledge : undefined,
                  } : undefined}
                  count={docs === null ? undefined : {
                    noun: 'documents',
                    n: filteredDocs.length,
                    m: docsBasis.length,
                  }}
                  refresh={(
                    <RefreshButton
                      ariaLabel="Refresh documents"
                      loading={docsLoading}
                      firstLoad={docsLoading && docs === null}
                      onClick={fetchDocs}
                    />
                  )}
                  query={{ chips: knowledgeQuery.chips, clearAll: clearKnowledge }}
                >
                  {knowledgeQuery.ignoredValues.length > 0 && (
                    <Alert severity="info" data-testid="ignoriert-hinweis" sx={{ mx: SP.card, mt: SP.tight }}>
                      {`Ignored unknown filter value “${knowledgeQuery.ignoredValues[0].value}”.`}
                    </Alert>
                  )}

                  {/* 🔴 R15-W10 (2026-10-31): `role="none"` gilt nur, solange
                      der `EmptyState error` darunter die Live-Region traegt —
                      der erscheint NUR beim Erstaufbau (`docs === null`).
                      Scheitert ein Nachladen bei vorhandenen Daten, ist dieser
                      Alert die einzige Flaeche; er sagt es dann selbst an. */}
                  {docsError && (
                    <Alert severity="error" role={docs === null ? 'none' : 'alert'} sx={{ mx: SP.card, mt: SP.tight }}>
                      {docsError}
                    </Alert>
                  )}

                  {docsLoading && docs === null && (
                    <Box data-testid="liste-skelett" sx={{ mt: SP.tight }}>
                      <CardListSkeleton cards={3} lines={3} form="outlined" ariaLabel="Loading knowledge documents" />
                    </Box>
                  )}

                  {!docsLoading && docs !== null && filteredDocs.length === 0 && (
                    <Box sx={{ py: SP.lg }}>
                      <EmptyState
                        reason={docsBasis.length === 0 ? 'no-data' : 'no-match'}
                        noun="document"
                        message={docsBasis.length === 0
                          ? 'No knowledge documents yet. Create the first one to power AI drafts.'
                          : undefined}
                        query={docsBasis.length === 0 ? undefined : (knowledgeQuery.state.q || undefined)}
                        onClearSearch={knowledgeQuery.state.q ? clearKnowledge : undefined}
                        // 🔴 R16-B2 (2026-10-31): der Ausweg haengt am GRUND, nicht
                        // nur an der Suche. Im `no-data`-Zweig gibt es nichts zu
                        // leeren — „Clear all filters" auf einer leeren Adresse
                        // aendert nichts, und der Satz daneben sagt „Create the
                        // first one". Hausform: `MediaLibraryPage`,
                        // `SourceManagerPage`, `RMACasesDashboardBody`
                        // (`isFiltered ? clearAllFilters : undefined`).
                        onClearFilters={docsBasis.length > 0 && !knowledgeQuery.state.q ? clearKnowledge : undefined}
                      />
                    </Box>
                  )}

                  {docsError && docs === null && (
                    <Box sx={{ py: SP.lg }}>
                      <EmptyState
                        reason="error"
                        message="Could not load knowledge documents."
                        detail={docsError}
                        onRetry={fetchDocs}
                      />
                    </Box>
                  )}

                  {docs !== null && filteredDocs.length > 0 && (
                    <Box data-testid="liste-karte" sx={{ mt: SP.tight }}>
                      <Stack spacing={SP.sm}>
                        {docsAufSeite.map(doc => (
                          <Paper key={doc.id} variant="outlined" sx={{ p: SP.card }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                              <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                                <Stack direction="row" spacing={SP.chip} alignItems="center" useFlexGap flexWrap="wrap">
                                  <Chip
                                    label={CATEGORIES.find(c => c.value === doc.category)?.label || doc.category}
                                    size="small"
                                    color={CATEGORY_COLORS[doc.category] || 'default'}
                                    sx={{ fontSize: FS.xs, height: 20 }}
                                  />
                                  {(() => {
                                    const level = doc.audience || AUDIENCE_DEFAULT
                                    const known = AUDIENCES.find(a => a.value === level)
                                    // An unrecognised value keeps the STRICTEST look, as before.
                                    const spec = known || AUDIENCES[0]
                                    const label = known ? known.label : level
                                    const meaning = known
                                      ? known.meaning
                                      : 'not a known level — treated as Internal'
                                    const line = audienceLine(label, meaning)
                                    return (
                                      // MUI `<Tooltip>` — the pattern the rest of this file uses.
                                      // The native `title` it replaces never reached the
                                      // accessibility tree, could not be opened from the keyboard,
                                      // and repeated all three definitions at every one of the
                                      // ~290 chips instead of the one it belongs to.
                                      <Tooltip title={line}>
                                        <Chip
                                          icon={<spec.Icon />}
                                          label={`Audience: ${label}`}
                                          size="small"
                                          // `role="img"` + `aria-label`: a bare chip is a generic
                                          // DIV, which may not carry a name. The row used to read
                                          // "FAQ Customer" as ONE nameless text node, with nothing
                                          // saying which word named which axis.
                                          role="img"
                                          aria-label={line}
                                          tabIndex={0}
                                          sx={audienceChipSx(spec.tone)}
                                        />
                                      </Tooltip>
                                    )
                                  })()}
                                  {/* 🔴 P22: der Status traegt ein WORT (Regel 22 Nr. 2 —
                                      nie Farbe allein). Bis G14b-2 stand hier nur ein
                                      „Inactive"-Chip fuer die inaktive Zeile; die Marke
                                      `karte-status-*` fehlte ganz, und die Probe waere
                                      dort vakuos gruen gewesen (Grill B1). */}
                                  <Box component="span" data-testid={`karte-status-${doc.id}`}>
                                    <Typography
                                      component="span"
                                      sx={{ fontSize: FS.xs, color: doc.is_active ? 'text.secondary' : v2Tone(theme, 'warn').main }}
                                    >
                                      {doc.is_active ? 'Active' : 'Inactive'}
                                    </Typography>
                                  </Box>
                                  {doc.token_estimate && (
                                    <Typography sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
                                      ~{doc.token_estimate} tokens
                                    </Typography>
                                  )}
                                </Stack>
                                <Typography data-testid={`karte-titel-${doc.id}`} sx={{ fontSize: FS.sm, fontWeight: 600 }}>
                                  {doc.title}
                                </Typography>
                                <Typography sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
                                  {/* Lehre 3: der Kennungs-Slot traegt NUR die Kennung,
                                      Monospace, nie gekuerzt. */}
                                  <Box component="span" data-testid={`karte-kennung-${doc.id}`} sx={{ fontFamily: 'monospace' }}>
                                    {doc.id}
                                  </Box>
                                  {' · '}
                                  Updated <CaseTime value={doc.updated_at} variant="absolute" precision="date" />
                                  {doc.updated_by ? ` by ${doc.updated_by}` : ''}
                                  {doc.source_url ? ' · synced from web' : ''}
                                </Typography>
                              </Stack>
                              <Stack direction="row" spacing={0} sx={{ flexShrink: 0 }}>
                                {/* P19: EINE Aktionsflaeche je Karte, mit Zeilenbezug im
                                    Namen. „Deactivate" bleibt ein Soft-Delete und
                                    behaelt seinen Rueckfrageweg — jetzt ein Dialog
                                    (ein `InlineConfirmButton` kann nicht in einem
                                    Menue stehen). */}
                                <RowActionsMenu
                                  label={`Actions for ${doc.title}`}
                                  actions={[
                                    {
                                      key: 'edit', label: 'Edit', icon: <EditIcon fontSize="small" />,
                                      onClick: () => { setEditDoc(doc); setDocDialogOpen(true) },
                                    },
                                    {
                                      key: 'deactivate', label: 'Deactivate',
                                      onClick: () => setBestaetigen({ art: 'doc', id: doc.id, name: doc.title }),
                                    },
                                  ]}
                                />
                              </Stack>
                            </Stack>
                            <Typography
                              sx={{
                                fontSize: FS.xs, color: 'text.secondary', mt: 0.5, whiteSpace: 'pre-wrap',
                                // 🔴 R16-W3 (2026-10-31): `PageContainer width="full"`
                                // laesst die Karte bei 1440/1920 ueber die ganze
                                // Breite laufen; der Text stand dann in Zeilen von
                                // 150–250 Zeichen. Die Grenze sitzt am TEXTBLOCK
                                // (in `ch`, also an der Schriftmetrik), nicht an
                                // der Karte — die bleibt breit.
                                maxWidth: '90ch',
                                // Befund 11b (2026-10-06): `maxHeight: 60` schnitt mitten in
                                // die vierte Zeile (FS.xs ≈ 11,5 px, Zeilenhoehe ≈ 1,43 →
                                // 16,4 px; 60 px endet bei 3,65 Zeilen), und das mitgegebene
                                // `textOverflow: 'ellipsis'` ist auf einem umbrechenden Block
                                // wirkungslos. Jetzt ein zeilenbasierter Clamp: DREI ganze
                                // Zeilen statt 3,65.
                                display: '-webkit-box', WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical', overflow: 'hidden',
                              }}
                            >
                              {doc.body?.slice(0, 300)}
                            </Typography>
                          </Paper>
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {docs !== null && filteredDocs.length > 0 && (
                    <Box data-testid="liste-fuss">
                      <TableFooterSimple
                        count={filteredDocs.length}
                        page={docsSeite - 1}
                        onPageChange={(p) => knowledgeQuery.set('page', p + 1)}
                        rowsPerPage={SEITENGROESSE}
                        // BS17-Opt-in: mobil steht die Leiste im Fluss (N9.11).
                        stickyOnMobile={false}
                        leftSlot={`~${totalTokens} tokens, all active documents`}
                      />
                    </Box>
                  )}
                </ListToolbar>
              </Stack>
            )}

            {/* ── Tone Examples tab ───────────────────────────────────────────── */}
            {tab === 'tone' && (
              <Stack spacing={SP.card}>
                {/* How it works banner */}
                <Alert severity="info" sx={{ fontSize: FS.xs }}>
                  <strong>How to feed the AI:</strong> Forward any past customer thread to{' '}
                  <code>service@brewout.de</code> with <code>[TONE]</code> anywhere in the subject line.
                  The system parses the thread, detects your reply, and queues it here for your review before it becomes a tone example.
                </Alert>

                {pullStatus && (
                  <Alert
                    severity={pullStatus.error ? 'error' : 'success'}
                    onClose={() => setPullStatus(null)}
                    sx={{ fontSize: FS.xs }}
                  >
                    {pullStatus.error
                      ? pullStatus.error
                      : `Pulled ${pullStatus.processed ?? 0} new thread(s). Skipped ${pullStatus.skipped ?? 0}.${pullStatus.errors?.length ? ` Errors: ${pullStatus.errors.length}.` : ''}`}
                  </Alert>
                )}

                <ListToolbar
                  shortcut="/"
                  search={{
                    value: toneQuery.state.q ?? '',
                    onChange: (v) => toneQuery.set('q', v),
                    onCommit: (v) => toneQuery.set('q', v),
                    placeholder: 'Search examples…',
                    label: 'Search examples',
                    inputRole: 'searchbox',
                    disabled: Boolean(examplesError) && examples === null,
                    sx: { flex: 1, minWidth: 0 },
                  }}
                  facets={schmal ? undefined : [
                    <BottomSheetSelect
                      key="inquiry_type"
                      label="Inquiry type"
                      value={toneQuery.state.inquiry_type ?? ''}
                      onChange={(v) => toneQuery.set('inquiry_type', v || null)}
                      options={INQUIRY_TYPES}
                      disabled={Boolean(examplesError) && examples === null}
                    />,
                    <BottomSheetSelect
                      key="language"
                      label="Language"
                      value={toneQuery.state.language ?? ''}
                      onChange={(v) => toneQuery.set('language', v || null)}
                      options={[{ value: 'de', label: 'DE' }, { value: 'en', label: 'EN' }]}
                      disabled={Boolean(examplesError) && examples === null}
                    />,
                  ]}
                  moreFilters={schmal ? {
                    // R16-W1/W5: siehe Dokumente-Reiter.
                    disabled: Boolean(examplesError) && examples === null,
                    resultCount: examples === null ? null : filteredExamples.length,
                    criteria: [
                      {
                        key: 'inquiry_type', label: 'Inquiry type',
                        value: toneQuery.state.inquiry_type ?? '',
                        onChange: (v) => toneQuery.set('inquiry_type', v || null),
                        options: INQUIRY_TYPES,
                        disabled: Boolean(examplesError) && examples === null,
                      },
                      {
                        key: 'language', label: 'Language',
                        value: toneQuery.state.language ?? '',
                        onChange: (v) => toneQuery.set('language', v || null),
                        options: [{ value: 'de', label: 'DE' }, { value: 'en', label: 'EN' }],
                        disabled: Boolean(examplesError) && examples === null,
                      },
                    ],
                    // 🔴 R15-W3 Runde 2 (2026-10-31): am FILTERZUSTAND, wie auf
                    // dem Dokumente-Reiter — nicht am Bestand.
                    onClearAll: toneQuery.chips.length > 0 ? clearTone : undefined,
                  } : undefined}
                  count={examples === null ? undefined : {
                    // 🔴 R15-W2 / R16-W4 (2026-10-31): die Bezugsmenge ist die
                    // KARTENLISTE (aktive Beispiele) — Seitenwechsel und
                    // Fusszeile beschreiben dieselbe Menge. Die wartenden
                    // Beispiele stehen als eigener Abschnitt darueber und
                    // tragen ihre Zahl in der Ueberschrift („Pending Review
                    // (n)"). Ohne den Zusatz hiess es „0 of 2 examples", obwohl
                    // darueber eine passende Pending-Zeile stand: beide Mengen
                    // heissen sonst gleich, und der Zaehler sagte die Unwahrheit
                    // ueber seinen Bezug. Den ganzen Reiter zu zaehlen waere die
                    // andere Aufloesung — sie widerstreitet der Seitenzahl der
                    // Kartenliste.
                    noun: 'active examples',
                    n: toneAktivGefiltert.length,
                    m: toneAktivBasis.length,
                  }}
                  refresh={(
                    <RefreshButton
                      ariaLabel="Refresh examples"
                      loading={examplesLoading}
                      firstLoad={examplesLoading && examples === null}
                      onClick={fetchExamples}
                    />
                  )}
                  query={{ chips: toneQuery.chips, clearAll: clearTone }}
                >
                  {toneQuery.ignoredValues.length > 0 && (
                    <Alert severity="info" data-testid="ignoriert-hinweis" sx={{ mx: SP.card, mt: SP.tight }}>
                      {`Ignored unknown filter value “${toneQuery.ignoredValues[0].value}”.`}
                    </Alert>
                  )}

                  {/* R15-W10: siehe Dokumente-Reiter — beim Nachladen mit
                      Bestand ist dieser Alert die einzige Live-Region. */}
                  {examplesError && (
                    <Alert severity="error" role={examples === null ? 'none' : 'alert'} sx={{ mx: SP.card, mt: SP.tight }}>
                      {examplesError}
                    </Alert>
                  )}

                  {examplesLoading && examples === null && (
                    <Box data-testid="liste-skelett" sx={{ mt: SP.tight }}>
                      <CardListSkeleton cards={3} lines={3} form="outlined" ariaLabel="Loading tone examples" />
                    </Box>
                  )}

                  {!examplesLoading && examples !== null && filteredExamples.length === 0 && (
                    <Box sx={{ py: SP.lg }}>
                      <EmptyState
                        reason={examplesBasis.length === 0 ? 'no-data' : 'no-match'}
                        noun="example"
                        message={examplesBasis.length === 0
                          ? 'No tone examples yet. Forward a thread to service@brewout.de with [TONE] in the subject, then click "Pull tone inbox now".'
                          : undefined}
                        query={examplesBasis.length === 0 ? undefined : (toneQuery.state.q || undefined)}
                        onClearSearch={toneQuery.state.q ? clearTone : undefined}
                        // R16-B2: siehe Dokumente-Reiter — im `no-data`-Zweig
                        // gibt es keinen Filter zu leeren.
                        onClearFilters={examplesBasis.length > 0 && !toneQuery.state.q ? clearTone : undefined}
                      />
                    </Box>
                  )}

                  {examplesError && examples === null && (
                    <Box sx={{ py: SP.lg }}>
                      <EmptyState
                        reason="error"
                        message="Could not load tone examples."
                        detail={examplesError}
                        onRetry={fetchExamples}
                      />
                    </Box>
                  )}

                  {/* Pending review section — eine DATENLAGE, kein Filter
                      (§1.5 Nr. 13): die Zahl steht in der Ueberschrift. */}
                  {examples !== null && tonePendingGefiltert.length > 0 && (
                    <Box sx={{ mt: SP.tight }}>
                      <Typography variant="subtitle2" sx={{ fontSize: FS.md, mb: SP.tight }}>
                        Pending Review ({tonePendingGefiltert.length})
                      </Typography>
                      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        {tonePendingGefiltert.map((ex, i) => {
                          const summary = (() => {
                            if (!ex.llm_thread_summary) return null
                            try {
                              return typeof ex.llm_thread_summary === 'string'
                                ? JSON.parse(ex.llm_thread_summary) : ex.llm_thread_summary
                            } catch { return null }
                          })()
                          const flagCount = (ex.llm_parsing_flags || []).length
                          const msgCount = (ex.full_thread || []).length
                          return (
                            <Box
                              key={ex.id}
                              // 🔴 R16-W2 (2026-10-31): die Zeile war NUR mit der
                              // Maus zu oeffnen — ohne `role`/`tabIndex` erreicht
                              // die Tastatur den Pruefdialog nicht. Jetzt eine
                              // bedienbare Zeile mit eigenem Namen; Enter und
                              // Leertaste oeffnen wie ein Klick (Leertaste
                              // scrollt dabei nicht, deshalb `preventDefault`).
                              role="button"
                              tabIndex={0}
                              data-testid="pending-zeile"
                              // 🔴 R16-W1 Runde 2 (2026-10-31): `role="button"`
                              // an der Zeilenwurzel macht alle Nachfahren
                              // PRAESENTATIONAL, und `aria-label` ersetzt den
                              // Namen ohnehin — der ⚠-Chip und „n msg" waren im
                              // A11y-Baum nicht vorhanden. Der Name traegt die
                              // Metadaten jetzt selbst.
                              //
                              // 🔴 R16-W2 Runde 3 (2026-10-31): das ist eine
                              // BENANNTE ABWEICHUNG von der Hausform, keine
                              // Umsetzung von ihr. `RMACasesDashboardBody.jsx`
                              // loest es ueber den TITEL als echten `<button>`
                              // und laesst die Zeilenwurzel Flaeche sein; hier
                              // bleibt die WURZEL der Knopf (eine Zeile, ein
                              // Ziel, kein zweiter Fokuspunkt), deshalb MUSS der
                              // Name tragen, was sonst praesentational waere:
                              // Titel, Typ · Sprache, Nachrichten, Flags, Datum
                              // (der sichtbare Titel steht vorn, WCAG 2.5.3).
                              aria-label={[
                                `Review ${ex.notes?.replace('Imported from forwarded thread: ', '') || 'Untitled thread'}`,
                                [summary?.topic || ex.inquiry_type, ex.language?.toUpperCase()].filter(Boolean).join(' · '),
                                `${msgCount} message${msgCount === 1 ? '' : 's'}`,
                                flagCount > 0 ? `${flagCount} parsing flag${flagCount === 1 ? '' : 's'}` : null,
                                ex.created_at ? formatDate(ex.created_at) : null,
                              ].filter(Boolean).join(', ')}
                              onClick={() => { setReviewExample(ex); setReviewOpen(true) }}
                              onKeyDown={(ev) => {
                                if (ev.key !== 'Enter' && ev.key !== ' ') return
                                ev.preventDefault()
                                setReviewExample(ex)
                                setReviewOpen(true)
                              }}
                              sx={{
                                display: 'flex', alignItems: 'center', px: SP.card, py: SP.sm, gap: 1.5,
                                borderTop: i > 0 ? '1px solid' : 'none', borderColor: 'divider',
                                cursor: 'pointer', ...hoverOnly({ bgcolor: 'action.hover' }),
                                '&:focus-visible': focusRing(),
                              }}
                            >
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontSize: FS.base, fontWeight: 500 }} noWrap>
                                  {ex.notes?.replace('Imported from forwarded thread: ', '') || 'Untitled thread'}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
                                  {summary?.topic || ex.inquiry_type} · {ex.language?.toUpperCase()}
                                </Typography>
                              </Box>
                              <Chip label={`${msgCount} msg`} size="small" sx={{ height: 20, fontSize: FS.xs }} />
                              {flagCount > 0 && (
                                <Chip
                                  icon={<WarningAmberIcon sx={{ fontSize: 12 }} />}
                                  label={flagCount} size="small" color="warning"
                                  sx={{ height: 20, fontSize: FS.xs }}
                                />
                              )}
                              <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                {ex.created_at
                                  ? <CaseTime value={ex.created_at} variant="absolute" precision="date" />
                                  : ''}
                              </Typography>
                            </Box>
                          )
                        })}
                      </Paper>
                      {/* 🔴 R16-W5 Runde 4 (2026-11-01): die Linie gehoert zum
                          Abschnitt DARUNTER und faellt mit ihm. Bei null
                          aktiven Beispielen kuendigte sie einen Abschnitt an,
                          der nicht kommt (Fotos `tone-nur-pending`); der
                          Zaehler nennt die Null, die Linie versprach trotzdem
                          etwas. */}
                      {toneAktivGefiltert.length > 0 && (
                        <Divider data-testid="tone-trenner" sx={{ my: SP.sm }} />
                      )}
                    </Box>
                  )}

                  {/* Active examples — die Kartenliste, seitenweise. */}
                  {examples !== null && toneAktivGefiltert.length > 0 && (
                    // 🔴 R16-C1 Runde 3 (2026-10-31): der Block traegt den
                    // Abstand nach OBEN (`mt`), nicht die Liste darunter — sonst
                    // haengt die Ueberschrift ohne Pending-Block (Filter blendet
                    // alle wartenden Beispiele aus) mit 0 px unter der
                    // Suchzeile, und mit Pending-Block steht sie naeher an ihm
                    // als an ihrer Liste (oben `mb` + `mt` = 16 px). Dieselbe
                    // Form wie der Pending-Block `:1165`.
                    <Box sx={{ mt: SP.tight }}>
                      {/* 🔴 R16-W2 Runde 2 (2026-10-31): Zaehler und Liste sind
                          auf dem Ton-Reiter durch den Pending-Block getrennt;
                          das Substantiv im Zaehler trug die Trennung allein.
                          Die Ueberschrift ist ZAHLFREI — ein zweiter Zaehler
                          derselben Menge verstiefse gegen P8 (genau EIN
                          Zaehler je Reiter), die blosse Beschriftung nicht.
                          Spiegelbild zu „Pending Review (n)" darueber. */}
                      <Typography variant="subtitle2" sx={{ fontSize: FS.md, mb: SP.tight }}>
                        Active examples
                      </Typography>
                      <Box data-testid="liste-karte">
                        <Stack spacing={SP.sm}>
                          {toneAufSeite.map(ex => (
                            <ToneExampleCard
                              key={ex.id}
                              example={ex}
                              onToggleFeatured={handleToggleFeatured}
                              onDeactivate={(id) => setBestaetigen({ art: 'example', id, name: typLabel(ex.inquiry_type) })}
                            />
                          ))}
                        </Stack>
                      </Box>
                    </Box>
                  )}

                  {examples !== null && toneAktivGefiltert.length > 0 && (
                    <Box data-testid="liste-fuss">
                      <TableFooterSimple
                        count={toneAktivGefiltert.length}
                        page={toneSeite - 1}
                        onPageChange={(p) => toneQuery.set('page', p + 1)}
                        rowsPerPage={SEITENGROESSE}
                        stickyOnMobile={false}
                      />
                    </Box>
                  )}
                </ListToolbar>
              </Stack>
            )}
          </Box>
        </UnderlineTabs>
      </Box>

      {/* ── Dialogs ─────────────────────────────────────────────────────────── */}
      <DocEditDialog
        open={docDialogOpen}
        doc={editDoc}
        onClose={() => setDocDialogOpen(false)}
        onSaved={fetchDocs}
      />

      {/* Thread import dialog */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: FS.base, fontWeight: 600 }}>Import Email Thread</DialogTitle>
        <DialogContent>
          <Stack spacing={SP.md} pt={1}>
            {importError && <Alert severity="error" onClose={() => setImportError(null)}>{importError}</Alert>}
            <Typography sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
              Paste a JSON array of thread messages. Each message: <code>{'{"role": "customer"|"samuel", "body": "…", "ts": "…"}'}</code>
              <br />The system creates one tone example per Samuel message, using the preceding customer message as context.
            </Typography>
            <Stack direction="row" spacing={SP.sm}>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Inquiry type</InputLabel>
                <Select
                  label="Inquiry type"
                  value={importForm.inquiry_type}
                  onChange={e => setImportForm(f => ({ ...f, inquiry_type: e.target.value }))}
                >
                  {INQUIRY_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Language</InputLabel>
                <Select
                  label="Language"
                  value={importForm.language}
                  onChange={e => setImportForm(f => ({ ...f, language: e.target.value }))}
                >
                  <MenuItem value="de">DE</MenuItem>
                  <MenuItem value="en">EN</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Notes (optional)"
                value={importForm.notes}
                onChange={e => setImportForm(f => ({ ...f, notes: e.target.value }))}
                size="small"
                sx={{ flex: 1 }}
              />
            </Stack>
            <TextField
              label="Thread JSON"
              value={importForm.thread_json}
              onChange={e => setImportForm(f => ({ ...f, thread_json: e.target.value }))}
              multiline
              minRows={8}
              fullWidth
              inputProps={{ style: { fontSize: FS.xs, fontFamily: 'monospace' } }}
              placeholder={'[{"role":"customer","body":"…","ts":""},{"role":"samuel","body":"…","ts":""}]'}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)} size="small">Cancel</Button>
          <DisabledReason
            reason={importing
              ? null
              : !importForm.thread_json.trim() ? 'Paste the thread JSON first.' : null}
          >
            <Button
              variant="contained"
              onClick={handleImportThread}
              disabled={importing || !importForm.thread_json.trim()}
              size="small"
              startIcon={importing ? <CircularProgress size={14} color="inherit" /> : null}
            >
              {importing ? 'Importing…' : 'Import'}
            </Button>
          </DisabledReason>
        </DialogActions>
      </Dialog>

      <ToneReviewPanel
        open={reviewOpen}
        onClose={() => { setReviewOpen(false); setReviewExample(null) }}
        example={reviewExample}
        onApproved={() => { setReviewOpen(false); setReviewExample(null); fetchExamples() }}
        onRejected={() => { setReviewOpen(false); setReviewExample(null); fetchExamples() }}
      />

      {/* Rueckfrage der Ruecknahme (Soft-Delete, E2: neutral, kein Papierkorb) */}
      <ConfirmDialog
        open={Boolean(bestaetigen)}
        title={bestaetigen?.art === 'doc' ? 'Deactivate document?' : 'Deactivate tone example?'}
        message={bestaetigen?.art === 'doc'
          ? `“${bestaetigen?.name}” stays in the list as inactive. You can make it active again in the editor.`
          : `“${bestaetigen?.name}” stays in the list as inactive.`}
        confirmLabel="Deactivate"
        cancelLabel="Cancel"
        onConfirm={bestaetigungAusfuehren}
        onCancel={() => setBestaetigen(null)}
      />

    </PageContainer>
  )
}
